import { useState, useMemo, useCallback, useEffect, useRef, useId } from 'react'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import useLatestRun, { ReopenTargetContext } from '@hooks/useLatestRun'
import { savedRunErrorSentence } from '@components/reports/queue/resultRetention'
import exagoReportService, { exagoRefusal, EXAGO_INFO_TIMEOUT_MS } from '@api/v1/exagoReportService'
import reportJobService from '@api/v1/reportJobService'
import { getTenant, getTenantApiHost } from '@config/tenant'
import SpecializedReportShell from '@components/reports/SpecializedReportShell'
import SpecializedFilterBar from '@components/reports/SpecializedFilterBar'
import ReportGroupedGrid from '@components/reports/ReportGroupedGrid'
import ReportViewsMenu from '@components/reports/ReportViewsMenu'
import useSavedReportView from '@components/reports/useSavedReportView'
import { useReportExportView } from '@components/reports/reportExportViewContext'
import { PENDO_IGNORE } from '@/analytics/domPrivacy'
import { buildGenericGridReport, percentLineRows } from './exagoGenericGrid'
import {
  ADVANCED_REPORT_NAME,
  FILTER_LOGIC_NOTE,
  SAVED_IN_REPORT,
  SAVED_LOCKED_NOTE,
  dateMode,
  derivedParts,
  describeGenericFilters,
  genericScheduleDatesNote,
  takesNoDates,
  infoFromRun,
  liveDateParts,
  percentLinesSentence,
  printSubtitleFor,
  promptDefaults,
  promptFieldsFor,
  ranDateParts,
  runDates,
  runProblem,
  savedFilterText,
  sortSentence,
  usesTodaySentence,
} from './exagoPromptFields'
import {
  exagoJobReportId,
  exagoQueueEndpoint,
  readSavedExagoRun,
  rollsWithRun,
} from './exagoAgedArJob'

/**
 * Spec 209 §10: any Advanced (Exago) report that is not the reviewed one (spec 190), run
 * through CC-53794 and drawn in the universal grouped grid from cb-api's generic reader.
 *
 * The filter bar is Exago's own dates — one "Date range" when the report has both, else one
 * date (spec 214 §3.1); every other prompt runs at the report's saved value and is stated as
 * a locked chip under the bar, "Saved in this report" (spec 214 §3.4). The grid is Exago's
 * printed layout, totals as printed; nothing is computed for display (exagoGenericGrid.js).
 * A report cb-api cannot prove is refused with the reason and a way to open it in Advanced
 * Reports, never drawn approximately.
 *
 * Mounted by ExagoReportRoute only: handed `/info` for a live open, or the saved body for a
 * `?jobId=` reopen. The document title is set by SpecializedReportShell (useDocumentTitle),
 * as on every specialized page.
 */

const todayIso = () => dayjs().format('YYYY-MM-DD')

// Module-level so useLatestRun's `run` keeps one identity (spec 131). cb-api's codes map to
// the page's own sentences (exagoRefusal); a failure that is not cb-api's says its message.
const runErrorMessage = (err) => exagoRefusal(err)?.sentence || err?.message || 'The report could not be run.'

/*
 * A failed `?jobId=` reopen is about the SAVED run, not a run of the report, so it says what
 * happened to that run, as spec 190's page does: spec 152's sentence (410 gone, 404 "Job not
 * found", a run of another report), else cb-api's own words for it (403 EXAGO_OWNER_ONLY's
 * `error` is cb-api's EXAGO_OWNER_ONLY_MESSAGE), else the page's own message above.
 */
const reopenErrorMessage = (err) => {
  const said = err?.response?.data?.error
  return savedRunErrorSentence(err) || (typeof said === 'string' && said.trim() ? said : null) || runErrorMessage(err)
}

/**
 * What a refusal offers: always "Try again"; for a 422 (the report runs in Advanced Reports
 * but Report Center will not draw it), a new-tab hand-off to Core's Advanced Reports page —
 * the hash route `https://<tenant>.centerbase.com/web/#advanced-reports-home` (spec 214 §3.6;
 * Core's `cbExagoReportsHomeView`, the same place LegacyEmbedReport's Exago entry opens).
 * The sentence itself is the caller's (the shell's alert band).
 *
 * Finding 22: on a shared host the tenant is the remembered site, which `/login?changesite=1`
 * clears while the user stays signed in; the href would then be `https://.centerbase.com/…`.
 * So the link is offered only when the tenant is known, and is left out otherwise.
 */
export const ExagoRefusalPanel = ({ refusal, onRetry }) => {
  const href = getTenant() ? `https://${getTenantApiHost()}/web/#advanced-reports-home` : null
  return (
    <div
      className="mb-4 rounded-rc-card border border-rc-line bg-white p-4 text-sm text-rc-ink-2"
      data-testid="exago-refusal"
      data-code={refusal?.code || undefined}
    >
      {refusal?.handOff && (
        <p className="mb-3">Advanced Reports can still run it: Exago prints the report there itself.</p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className="btn-rc" onClick={onRetry}>Try again</button>
        {refusal?.handOff && href && (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-rc"
            aria-label="Open Advanced Reports (opens in a new tab)"
          >
            Open Advanced Reports
          </a>
        )}
      </div>
    </div>
  )
}

// Publish the grid's export view to the shell (CSV / XLSX / PDF of what is on screen), as
// spec 190's page does: a rendered child of the shell, where the context provider lives.
const PublishedGrid = ({ report, storageKey, pageStateRef, initialPageState }) => {
  const exportCtx = useReportExportView()
  return (
    <ReportGroupedGrid
      report={report}
      storageKey={storageKey}
      exportViewRef={exportCtx?.ref}
      pageStateRef={pageStateRef}
      initialPageState={initialPageState}
      showZeroToggle={false}
      showInactiveToggle={false}
    />
  )
}

// "N rows in G groups" names the TOP-LEVEL groups the grid opens with. `counts.groups`
// counts every level (2 timekeepers holding 3 clients is 5), so it only says WHETHER
// there are groups (`hasGroups` below), never how many this sentence states.
const topLevelGroups = (tree) => (tree || []).filter((n) => Array.isArray(n?.children)).length
const countsPhrase = (records, groups) =>
  `${records} row${records === 1 ? '' : 's'}${groups > 0 ? ` in ${groups} group${groups === 1 ? '' : 's'}` : ''}`

/**
 * What the run ran with and how Exago shaped it — React text only (spec 209 §10). It
 * carries firm data (saved values, Exago's header text), so Pendo never captures it.
 *
 * Finding 18: after a later run fails, the previous run stays on screen labelled by its own
 * dates, but its row count does not: under the error band a "0 rows" reads as the failed
 * run's answer. A count is stated only while no error is showing.
 */
const GenericRunNotes = ({ result, error }) => {
  const p = result.parameters
  const dates = ranDateParts(p)
  const derived = derivedParts(p.derived)
  const sorts = sortSentence(result.notes)
  return (
    <div className={`${PENDO_IGNORE} mb-3 space-y-1 text-xs text-rc-muted`} data-testid="exago-run-notes">
      <p>
        <span className="font-semibold text-rc-ink-2">Run in Exago</span>
        {dates.length > 0 && <> · {dates.join(' · ')}</>}
        {result.source?.elapsedMs != null && <> · took {(result.source.elapsedMs / 1000).toFixed(1)} s</>}
        {!error && <>{' '}· {countsPhrase(result.counts.records, topLevelGroups(result.tree))}</>}
      </p>
      {derived.length > 0 && <p data-testid="exago-derived">{derived.join(' · ')}</p>}
      {/* No TopN line: every live report carries a TopN (top 10), and whether it is
          enabled cannot be read (notes.topNUnread, spec 214 §2.8), so nothing is re-added
          and no limit is stated — stating one is an open question for Andrew (spec 214 §4). */}
      {sorts && <p data-testid="exago-sorts">{sorts}</p>}
      {/* "…not from these dates" only when these notes state one of the run's dates. */}
      {result.notes.usesToday && <p data-testid="exago-uses-today">{usesTodaySentence(dates.length > 0)}</p>}
      {result.headerLines.length > 0 && (
        <p className={PENDO_IGNORE} data-testid="exago-header-lines">
          <span className="font-semibold text-rc-ink-2">From the report’s header:</span> {result.headerLines.join(' · ')}
        </p>
      )}
    </div>
  )
}

const LockIcon = () => (
  <svg aria-hidden="true" focusable="false" className="h-3 w-3 shrink-0" viewBox="0 0 20 20" fill="currentColor">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10 1a4.5 4.5 0 0 0-4.5 4.5V9H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2h-.5V5.5A4.5 4.5 0 0 0 10 1Zm3 8V5.5a3 3 0 1 0-6 0V9h6Z"
    />
  </svg>
)

/**
 * Spec 214 §3.4: the report's saved prompts and filters, as locked chips under the filter
 * bar — "Label: value" in cb-api's words. Read-only: Report Center cannot set them yet
 * (spec 209 §12 Q2), and the list says so ONCE, as visible text after its heading, which is
 * also the list's accessible description. Not per chip: a title on each chip was hover-only,
 * and as a fallback name it gave every chip the same generic sentence.
 *
 * Firm data (filter names and values), so Pendo never captures it. The note is product
 * copy, but it sits inside the same PENDO_IGNORE block: nothing is lost by not capturing a
 * fixed sentence, and one ignored container cannot leak a chip. React text only.
 *
 * The note's colour, measured against what it actually sits on: the Layout's `bg-gray-50`
 * page (Tailwind v4 oklch(98.5% 0.002 247.839) = #f9fafb; no card behind the filter area),
 * in --color-rc-muted #6b7280 (src/index.css) — 4.626:1, clearing SC 1.4.3's 4.5:1 for
 * 12px text. On white it would be 4.834:1. Move it onto a darker fill and this must be
 * recomputed.
 *
 * Finding 0: when cb-api cannot read how the filters combine (`filterLogicUnread`: Exago's
 * And/Or and grouping are not in CC-54456's DesignInfo), FILTER_LOGIC_NOTE follows the note,
 * as visible text in the same class (the same 4.626:1), inside the same PENDO_IGNORE block,
 * and is part of the list's description too — so no reader takes the chips as limits that
 * all apply.
 */
const SavedFilterChips = ({ fixed, filterLogicUnread = false }) => {
  const headingId = useId()
  const noteId = useId()
  const logicId = useId()
  if (!Array.isArray(fixed) || fixed.length === 0) return null
  return (
    <div className={`${PENDO_IGNORE} mt-2 mb-3 flex flex-wrap items-center gap-1.5`} data-testid="exago-saved-filters">
      <p id={headingId} className="text-xs font-semibold text-rc-muted">{SAVED_IN_REPORT}</p>
      <p id={noteId} className="text-xs text-rc-muted" data-testid="exago-saved-note">{SAVED_LOCKED_NOTE}</p>
      {filterLogicUnread && (
        <p id={logicId} className="text-xs text-rc-muted" data-testid="exago-filter-logic-note">{FILTER_LOGIC_NOTE}</p>
      )}
      {/* role="list": Tailwind's preflight drops list-style, and Safari then drops list semantics. */}
      <ul
        role="list"
        aria-labelledby={headingId}
        aria-describedby={filterLogicUnread ? `${noteId} ${logicId}` : noteId}
        className="flex flex-wrap gap-1.5"
      >
        {fixed.map((p, i) => (
          <li
            key={i}
            className={`${PENDO_IGNORE} inline-flex items-center gap-1 rounded-full border border-rc-line bg-gray-50 px-2 py-0.5 text-xs text-rc-ink-2`}
            data-testid="exago-saved-filter"
          >
            <LockIcon />
            <span>{savedFilterText(p)}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * Exago's percent lines, in their own small table (spec 190's "% of total", generalised).
 * Not grid footer rows: under a money column the export writers would read "98.74%" as an
 * amount. As printed, React text.
 */
const PercentLines = ({ rows, columns }) => {
  if (!rows.length) return null
  const shown = [...new Set(rows.flatMap((r) => r.cells.map((c) => c.index)))].sort((a, b) => a - b)
  return (
    <table className={`${PENDO_IGNORE} mt-3 text-xs text-rc-ink-2`} data-testid="exago-percent-lines">
      <caption className="text-left font-semibold text-rc-ink-2 pb-1">Exago’s percentage lines, as Exago printed them</caption>
      <thead>
        <tr>
          <th scope="col" className="px-3 text-left font-medium text-rc-muted">Applies to</th>
          {shown.map((i) => <th key={i} scope="col" className="px-3 text-right font-medium text-rc-muted">{columns[i].header}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, n) => (
          <tr key={n}>
            <th scope="row" className="px-3 text-left font-normal">{r.scope ?? 'Whole report'}</th>
            {shown.map((i) => <td key={i} className="px-3 text-right tabular-nums">{r.cells.find((c) => c.index === i)?.text ?? ''}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const ExagoGenericPage = ({ exagoReportId, info: infoProp, jobId, savedBody }) => {
  const [filters, setFilters] = useState(() => promptDefaults(todayIso()))
  const [result, setResult] = useState(null)
  // Spec 192: when set, the result on screen is a saved run reopened from the queue.
  const [savedAsOf, setSavedAsOf] = useState(null)
  // The newest failed run's cb-api refusal (code, hand-off), for the panel under the error.
  const [refusal, setRefusal] = useState(null)
  // Consume-once: the first run reopens the queue's saved job; later runs are fresh.
  const jobRef = useRef(jobId || null)
  const savedBodyRef = useRef(savedBody ?? null)
  // Whether the newest run is the `?jobId=` reopen. Set synchronously when each run's work
  // starts, so useLatestRun's catch (which calls `errorMessage`) reads the run that failed,
  // including a failure in its own reopen guard (spec 168). Stable identity, as spec 131 asks.
  const reopeningRef = useRef(false)
  const errorMessage = useCallback((err) => (reopeningRef.current ? reopenErrorMessage(err) : runErrorMessage(err)), [])
  const view = useSavedReportView(setFilters)
  const {
    running, error, ran, runKey, run: startRun, invalidate: invalidateRun,
  } = useLatestRun({ initialRunning: !!jobId, errorMessage })

  // A reopen arrives without /info (the route never waits for it). Ask for it in the
  // background so the next run gets the report's CURRENT prompts; until then (or if it
  // fails) the saved run's own description serves.
  const infoQuery = useQuery({
    queryKey: ['exago-info', String(exagoReportId).toLowerCase()],
    queryFn: () => exagoReportService.info(exagoReportId),
    enabled: !infoProp,
    retry: false,
    staleTime: 60_000,
  })
  const fetchedInfo = infoQuery.data?.reviewed === false ? infoQuery.data : null
  const runInfo = useMemo(() => infoFromRun(result), [result])
  const description = infoProp || fetchedInfo || runInfo

  const fields = useMemo(() => promptFieldsFor(description), [description])
  // Spec 214 §3.1: which date control the report gets, and the label every statement uses.
  const dateControl = useMemo(() => dateMode(description), [description])
  const name = description?.report?.name || result?.report?.name || ADVANCED_REPORT_NAME
  const summary = description?.report?.description || undefined
  // Spec 209 §10: a report re-designed in Exago starts with fresh column preferences and
  // saved views rather than attaching old ones to moved columns.
  const signature = result?.layoutSignature || description?.layoutSignature || null
  const storageKey = signature ? `report:exago:${String(exagoReportId).toLowerCase()}:${signature}` : null

  const liveDates = useMemo(() => liveDateParts(filters, description), [filters, description])
  const filtersSummary = useMemo(() => describeGenericFilters({
    dates: liveDates,
    usesToday: description?.notes?.usesToday === true,
    fixed: description?.fixed,
    filterLogicUnread: description?.notes?.filterLogicUnread === true,
  }), [liveDates, description])
  const printSubtitle = printSubtitleFor(liveDates)

  const runReport = useCallback(() => {
    // A reopen reads the saved run; the filters on screen are not what it ran with.
    if (!jobRef.current) {
      const problem = runProblem(filters, dateControl)
      if (problem) {
        setRefusal(null)
        invalidateRun({ error: problem })
        return undefined
      }
    }
    const dates = runDates(filters)
    return startRun(async ({ isCurrent }) => {
      const jid = jobRef.current
      jobRef.current = null // consumed — subsequent runs are fresh
      reopeningRef.current = !!jid
      setRefusal(null)
      try {
        if (jid) {
          // Spec 192: the queue's "View report" → the saved run's own figures and run time.
          // `useLatestRun` (spec 168, via ReopenTargetContext below) refuses a run of
          // another report before this commits.
          const body = savedBodyRef.current
          savedBodyRef.current = null
          const [st, stored] = await Promise.all([reportJobService.status(jid), body ?? reportJobService.result(jid)])
          const data = readSavedExagoRun(stored)
          return () => { setSavedAsOf(st?.createdAt || null); setResult(data) }
        }
        const data = await exagoReportService.run(exagoReportId, dates, { generic: true })
        // Spec 131 decision 3: the saved-result banner clears at the COMMIT.
        return () => { setSavedAsOf(null); setResult(data) }
      } catch (err) {
        // A failed reopen records no refusal, so it offers no "Try again": that button starts
        // a FRESH run with the dates on screen, which beside "This saved result is no longer
        // stored…" reads as opening the saved run again. The filter bar's Run is the way on.
        // A live run's refusal keeps its panel. Not cb-api's (a network failure, a shape the
        // guard refused): still retryable.
        if (!jid && isCurrent()) setRefusal(exagoRefusal(err) || { code: null, status: null, handOff: false })
        throw err
      }
    }, {
      criteria: { filtersSummary, printSubtitle },
    })
  }, [filters, dateControl, exagoReportId, startRun, invalidateRun, filtersSummary, printSubtitle])

  // A reopen is a read, so it runs on arrival; a fresh run stays a click (Exago can be
  // slow). Deferred and cancellable (spec 131), so StrictMode's simulated remount reads once.
  useEffect(() => {
    if (!jobId) return undefined
    const t = setTimeout(() => { runReport() }, 0)
    return () => clearTimeout(t)
  }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  const gridReport = useMemo(() => buildGenericGridReport(result), [result])
  const percentRows = useMemo(() => percentLineRows(result), [result])
  // What the run on screen ran with; before a run, the report's own saved values — and,
  // the same way, whether how they combine is unread (finding 0).
  const savedFilters = result ? result.parameters.fixed : description?.fixed
  const runLogicFlag = result?.notes?.filterLogicUnread
  /*
   * Review finding 16: a run stored before cb-api sent the flag has no answer of its own. /info's
   * stands for it once /info has ANSWERED (a reopen asks it in the background); until then, and
   * for good if /info fails, the flag is unknown — and an unknown is not "not in doubt", so the
   * note shows whenever the run states at least one saved filter. Never the reopen's own
   * description (infoFromRun), which is the same flagless notes.
   */
  const infoLogicFlag = (infoProp || fetchedInfo)?.notes?.filterLogicUnread
  const filterLogicUnread = result
    ? (typeof runLogicFlag === 'boolean' ? runLogicFlag
      : typeof infoLogicFlag === 'boolean' ? infoLogicFlag
        : (result.parameters.fixed || []).some((p) => p?.source === 'filter'))
    : description?.notes?.filterLogicUnread === true
  // The documents' criteria line (CSV / XLSX / PDF / print / AI scope) states what the run
  // on screen SAYS it ran with — its own dates and labels, never the panel's.
  const documentFilters = useMemo(
    () => (result
      ? describeGenericFilters({
        dates: ranDateParts(result.parameters),
        derived: derivedParts(result.parameters.derived),
        usesToday: result.notes.usesToday,
        fixed: result.parameters.fixed,
        percents: percentLinesSentence(percentRows),
        filterLogicUnread,
      })
      : filtersSummary),
    [result, percentRows, filtersSummary, filterLogicUnread]
  )
  const documentPrintSubtitle = result ? printSubtitleFor(ranDateParts(result.parameters)) : printSubtitle

  const filterBar = (
    <SpecializedFilterBar
      fields={fields}
      filters={filters}
      onFiltersChange={setFilters}
      defaults={promptDefaults(todayIso())}
      onRun={runReport}
      isLoading={running}
      // Spec 192: run in the background, queue for later, or schedule — offered once the
      // report is described (an /info answer passed cb-api's list fence; a saved run was
      // the owner's). The replay path (spec 209 §10, D1) always carries the End Date
      // (`asOf`) and carries the Start Date (`earliest`) ONLY when the user chose one: a
      // blank start is left out, so each run uses CB's own default, never a 1900 baked in.
      queue={description ? {
        reportId: exagoJobReportId(exagoReportId),
        name,
        actionKey: 'exago',
        // cb-api's job / schedule door for a generic report can wait 5 s (the capability
        // probe) + 45 s (the list fence) before it answers; the browser's default 30 s
        // POST would give up on a create that succeeds. Same budget as /info (D3).
        timeoutMs: EXAGO_INFO_TIMEOUT_MS,
        // Finding 17: a run the page's own Run would refuse (a blank end date, a start after
        // the end) is refused here too, with the same sentence, before any endpoint is built —
        // never sent on to fail cb-api's replay check with a sentence about the wrong thing.
        // The filter bar shows a thrown message as its error toast and creates nothing.
        endpoint: (f, { mode } = {}) => {
          const problem = runProblem(f, dateControl)
          if (problem) throw new Error(problem)
          return exagoQueueEndpoint(exagoReportId, runDates(f), {
            // Spec 217: a report the run day changes nothing about stores a fixed date.
            rolling: mode === 'schedule' && rollsWithRun(f, todayIso()) && !takesNoDates(dateControl, description),
          })
        },
        // Finding 11: a null note means "say nothing". RecurrenceEditor replaces any falsy
        // note with "Every run repeats the date range shown above…", false for a report with
        // no dates, so nothing is handed over as an empty element instead.
        scheduleDatesNote: (f) => {
          const note = genericScheduleDatesNote(f, todayIso(), dateControl, description)
          return note === null ? <></> : note
        },
      } : undefined}
      actions={storageKey ? (
        <ReportViewsMenu
          storageKey={storageKey}
          currentFilters={filters}
          onApply={view.applyView}
          getPageState={view.getPageState}
        />
      ) : undefined}
    />
  )
  // Spec 214 §3.4: the saved-filter chips sit directly under the filter bar, so they stay
  // beside the dates while a run is out (the shell hides its children then).
  const filterArea = (
    <>
      {filterBar}
      <SavedFilterChips fixed={savedFilters} filterLogicUnread={filterLogicUnread} />
    </>
  )

  return (
    <SpecializedReportShell
      name={name}
      // Spec 209 §3: a firm-authored report name never reaches analytics.
      analyticsName={ADVANCED_REPORT_NAME}
      summary={summary}
      running={running}
      error={error}
      ranCriteria={ran?.criteria ? { ...ran.criteria, filtersSummary: documentFilters, printSubtitle: documentPrintSubtitle } : null}
      runKey={runKey}
      filterBar={filterArea}
      filtersSummary={filtersSummary}
      printSubtitle={printSubtitle}
      savedAsOf={savedAsOf}
      // A saved run's documents state ITS parameters, which its own answer carries.
      savedRunFiltersSummary={savedAsOf != null && result ? documentFilters : undefined}
      ready={!!result}
      hasGroups={result ? result.counts.groups > 0 : undefined}
    >
      {/* A failed run's way on. Not for a date the page refused itself, nor for a failed
          `?jobId=` reopen (no refusal is recorded for either): Try again would say the same
          sentence again, or start a fresh run that reads as re-opening the saved one. */}
      {error && refusal && <ExagoRefusalPanel refusal={refusal} onRetry={runReport} />}
      {!result && !running && !error && (
        <p className="rounded-rc-card border border-rc-line bg-white p-6 text-sm text-rc-muted" data-testid="exago-intro">
          Runs in Exago and shows its own subtotals and totals.
        </p>
      )}
      {result && (
        <>
          <GenericRunNotes result={result} error={!!error} />
          {/* Finding 18: "these parameters" under a failed run with changed filters reads as
              the failed run's answer, so the empty state is said only while no error shows. */}
          {result.empty ? (!error && (
            <p className="rounded-rc-card border border-rc-line bg-white p-6 text-sm text-rc-muted" data-testid="exago-empty">
              Exago found no rows for these parameters.
            </p>
          )) : (
            <PublishedGrid
              key={`${view.gridKey}:${storageKey}`}
              report={gridReport}
              storageKey={storageKey}
              pageStateRef={view.pageStateRef}
              initialPageState={view.initialPageState}
            />
          )}
          <PercentLines rows={percentRows} columns={result.columns} />
        </>
      )}
    </SpecializedReportShell>
  )
}

/**
 * Spec 168's reopen guard, as on spec 190's page: `useLatestRun` refuses a `?jobId=` run
 * whose report is not this one before anything commits.
 */
const ExagoGenericReport = ({ exagoReportId, info, jobId = null, savedBody }) => {
  const reportId = exagoJobReportId(exagoReportId)
  const reopenTarget = useMemo(() => (jobId ? { reportId, jobId } : null), [reportId, jobId])
  return (
    <ReopenTargetContext.Provider value={reopenTarget}>
      <ExagoGenericPage
        key={`${reportId}:${jobId || 'live'}`}
        exagoReportId={exagoReportId}
        info={info}
        jobId={jobId}
        savedBody={savedBody}
      />
    </ReopenTargetContext.Provider>
  )
}

export default ExagoGenericReport
